import { v4 as uuidv4 } from "uuid";
import { createApiWithNewBasePath as api, IProgressEvent, IResponse } from "@api";

export type PhotoAttachmentId = string | number;

export interface PhotoAttachment {
  id: PhotoAttachmentId;
  fullUrl?: string;
  [key: string]: unknown;
}

export interface PresignedUrlPayload {
  category: string;
  file_extension: string;
}

export interface PresignedUrlResponse {
  fileName: string;
  presignedUrl: string;
}

/**
 * Generic photo_attachments HTTP layer — category/photo_attachable_id are caller-supplied
 * instead of hardcoded to "portfolio"/Listworker (see ServiceProviders/services/photoBook.ts,
 * which this generalizes). Both "portfolio" (attached to a Listworker) and "task_photos"
 * (unattached until linked to a task later) are backend-supported categories.
 *
 * Every call goes to `api/public/photo_attachments`, not `api/photo_attachments`:
 * its only caller today is the task creation flow, whose whole point is a
 * logged-out visitor, and the private controller 401s one. The public
 * controller still identifies a signed-in caller and hands the same user to
 * `PhotoAttachmentAbility`, so a logged-in visitor is scoped exactly as before.
 */
const BASE_PATH = "public/photo_attachments";
export const getPhotoAttachmentPresignedUrlService = async (
  payload: PresignedUrlPayload
): Promise<PresignedUrlResponse> => {
  const { data } = await api.get<
    PresignedUrlPayload & { _: string },
    { data: PresignedUrlResponse }
  >(`${BASE_PATH}/new`, {
    ...payload,
    // Unique per call, ignored by the controller. Safari/WebKit answers
    // identical GETs that are in flight together with one shared response, so
    // photos picked at once were all handed the same file name and overwrote
    // each other (HUB-5628). `Date.now()` would not do: calls fired in the
    // same millisecond share it.
    _: uuidv4(),
  });

  return data?.data;
};

export const uploadPhotoAttachmentFileService = (
  url: string,
  file: File,
  onUploadProgress?: (progressEvent: IProgressEvent) => void
) =>
  api.put<File, IResponse>(url, file, {
    headers: {
      "Content-Type": file.type,
      Authorization: null,
    },
    onUploadProgress,
  });

export interface AttachPhotoAttachmentPayload {
  category: string;
  photo: string;
  /** Omitted for "task_photos" — it starts unattached and is linked to a
   * task later, via that task's own creation payload. */
  photo_attachable_id?: PhotoAttachmentId;
}

export const attachPhotoAttachmentService = async (
  payload: AttachPhotoAttachmentPayload
): Promise<PhotoAttachment> => {
  const result = await api.post<typeof payload, { data: PhotoAttachment }>(
    BASE_PATH,
    payload
  );

  return result?.data?.data;
};

export interface UpdatePhotoAttachmentPayload {
  position?: number;
  /** The caption. `photo_attachments` has no column of its own for it — the
   * controller permits exactly `metadata: [:description]`. */
  metadata?: { description?: string };
}

export const updatePhotoAttachmentService = async (
  id: PhotoAttachmentId,
  payload: UpdatePhotoAttachmentPayload
): Promise<PhotoAttachment> => {
  const result = await api.put<
    { photo_attachment: UpdatePhotoAttachmentPayload },
    { data: PhotoAttachment }
  >(`${BASE_PATH}/${id}`, { photo_attachment: payload });

  return result?.data?.data;
};

export const deletePhotoAttachmentService = (id: PhotoAttachmentId) =>
  api.delete(`${BASE_PATH}/${id}`);
